import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { soundManager } from "../lib/sound";
import { Sparkles, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { Player } from "../types";

export interface EmojiReaction {
  id: string;
  emoji: string;
  name: string;
  sound: "emoji_applause" | "emoji_angry" | "emoji_surprised" | "emoji_laughing" | "emoji_lucky";
  bgGradient: string;
  ringColor: string;
  glowShadow: string;
}

export const READY_EMOJIS: EmojiReaction[] = [
  {
    id: "applause",
    emoji: "👏",
    name: "Alkış",
    sound: "emoji_applause",
    bgGradient: "from-amber-500/30 to-yellow-600/30",
    ringColor: "hover:ring-amber-400 border-amber-500/40",
    glowShadow: "rgba(245, 158, 11, 0.4)",
  },
  {
    id: "angry",
    emoji: "😡",
    name: "Kızgın",
    sound: "emoji_angry",
    bgGradient: "from-red-500/30 to-rose-600/30",
    ringColor: "hover:ring-red-400 border-red-500/40",
    glowShadow: "rgba(239, 68, 68, 0.4)",
  },
  {
    id: "surprised",
    emoji: "😲",
    name: "Şaşkın",
    sound: "emoji_surprised",
    bgGradient: "from-purple-500/30 to-indigo-600/30",
    ringColor: "hover:ring-purple-400 border-purple-500/40",
    glowShadow: "rgba(168, 85, 247, 0.4)",
  },
  {
    id: "laughing",
    emoji: "😂",
    name: "Gülen",
    sound: "emoji_laughing",
    bgGradient: "from-yellow-400/30 to-amber-500/30",
    ringColor: "hover:ring-yellow-400 border-yellow-400/40",
    glowShadow: "rgba(234, 179, 8, 0.4)",
  },
  {
    id: "lucky",
    emoji: "🍀",
    name: "Şanslı",
    sound: "emoji_lucky",
    bgGradient: "from-emerald-500/30 to-green-600/30",
    ringColor: "hover:ring-emerald-400 border-emerald-500/40",
    glowShadow: "rgba(16, 185, 129, 0.4)",
  },
];

export interface ActiveBurst {
  id: string;
  emoji: string;
  x: number;
  y: number;
  label?: string;
  targetName?: string;
}

interface ReactionPanelProps {
  onSendReaction: (emoji: string, targetPlayerId?: string, label?: string) => void;
  players?: Player[];
  myId?: string;
  isLandscapeLayout?: boolean;
}

export const ReactionPanel: React.FC<ReactionPanelProps> = ({
  onSendReaction,
  players = [],
  myId,
  isLandscapeLayout = false,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [draggingEmoji, setDraggingEmoji] = useState<EmojiReaction | null>(null);
  const [hoveredTarget, setHoveredTarget] = useState<string | null>(null);
  const [activeBursts, setActiveBursts] = useState<ActiveBurst[]>([]);

  // Spawn visual burst particles at coordinate (x, y)
  const spawnBurst = (emoji: string, x: number, y: number, label?: string, targetName?: string) => {
    const burstId = `${Date.now()}-${Math.random()}`;
    setActiveBursts((prev) => [...prev, { id: burstId, emoji, x, y, label, targetName }]);
    setTimeout(() => {
      setActiveBursts((prev) => prev.filter((b) => b.id !== burstId));
    }, 2200);
  };

  // Find target element underneath drop point (card pile, player avatar, etc.)
  const detectDropTarget = (clientX: number, clientY: number): { type: "player" | "middlePile" | "table"; targetId?: string; targetName?: string } => {
    const elements = document.elementsFromPoint(clientX, clientY);
    for (const el of elements) {
      // Check for player avatars
      const playerEl = el.closest("[data-player-id]");
      if (playerEl) {
        const pId = playerEl.getAttribute("data-player-id");
        const foundPlayer = players.find((p) => p.id === pId);
        return { type: "player", targetId: pId || undefined, targetName: foundPlayer?.name || "Oyuncu" };
      }
      // Check for middle cards pile / table area
      if (el.closest("[data-middle-pile]") || el.closest(".fanned-hand-container") || el.closest(".card-play-area")) {
        return { type: "middlePile", targetName: "Masa & Kartlar" };
      }
    }
    return { type: "table" };
  };

  const handleEmojiClick = (reaction: EmojiReaction) => {
    soundManager.play(reaction.sound);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(20);
    }
    onSendReaction(reaction.emoji, undefined, reaction.name);

    // Spawn central celebration burst
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight * 0.55;
    spawnBurst(reaction.emoji, centerX, centerY, reaction.name);
  };

  const handleDragStart = (reaction: EmojiReaction) => {
    setDraggingEmoji(reaction);
    soundManager.play("emoji_pop");
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(15);
    }
  };

  const handleDrag = (_: any, info: any) => {
    const target = detectDropTarget(info.point.x, info.point.y);
    if (target.type === "player" && target.targetName) {
      setHoveredTarget(`👉 ${target.targetName}`);
    } else if (target.type === "middlePile") {
      setHoveredTarget("🎴 Masadaki Kartlar");
    } else {
      setHoveredTarget(null);
    }
  };

  const handleDragEnd = (_: any, info: any, reaction: EmojiReaction) => {
    const dropX = info.point.x;
    const dropY = info.point.y;
    const target = detectDropTarget(dropX, dropY);

    setDraggingEmoji(null);
    setHoveredTarget(null);

    soundManager.play(reaction.sound);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate([25, 30, 25]);
    }

    if (target.type === "player" && target.targetId) {
      onSendReaction(reaction.emoji, target.targetId, `${reaction.name} -> ${target.targetName}`);
      spawnBurst(reaction.emoji, dropX, dropY, reaction.name, target.targetName);
    } else if (target.type === "middlePile") {
      onSendReaction(reaction.emoji, undefined, `${reaction.name} (Masaya)`);
      spawnBurst(reaction.emoji, dropX, dropY, reaction.name, "Masa");
    } else {
      onSendReaction(reaction.emoji, undefined, reaction.name);
      spawnBurst(reaction.emoji, dropX, dropY, reaction.name);
    }
  };

  return (
    <>
      {/* Visual Burst Overlay: Renders animated floating & popping emoji reactions */}
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
        <AnimatePresence>
          {activeBursts.map((burst) => (
            <motion.div
              key={burst.id}
              initial={{ scale: 0.2, opacity: 0, x: burst.x, y: burst.y }}
              animate={{
                scale: [0.2, 1.4, 1.1, 1],
                opacity: [0, 1, 1, 0],
                y: burst.y - 120,
                rotate: [0, -10, 10, -5, 0],
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 1.8, ease: "easeOut" }}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center select-none"
            >
              {/* Floating main reaction bubble */}
              <div className="relative flex items-center justify-center p-3 rounded-3xl bg-slate-900/90 border border-white/20 shadow-[0_10px_30px_rgba(0,0,0,0.8)] backdrop-blur-md">
                <span className="text-4xl md:text-5xl filter drop-shadow-lg animate-bounce">
                  {burst.emoji}
                </span>
                {burst.targetName && (
                  <span className="absolute -bottom-5 px-2 py-0.5 bg-cyan-950/90 border border-cyan-500/40 text-cyan-300 text-[10px] font-black rounded-full whitespace-nowrap shadow-md">
                    {burst.targetName}
                  </span>
                )}
              </div>

              {/* Sparkles / Mini flying particle duplicates */}
              {[...Array(8)].map((_, i) => {
                const angle = (i / 8) * Math.PI * 2;
                const distance = 40 + Math.random() * 35;
                const px = Math.cos(angle) * distance;
                const py = Math.sin(angle) * distance;
                return (
                  <motion.span
                    key={i}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 0.8 }}
                    animate={{
                      x: px,
                      y: py - 20,
                      opacity: 0,
                      scale: 0.2,
                    }}
                    transition={{ duration: 1.2, delay: 0.1 + i * 0.05, ease: "easeOut" }}
                    className="absolute text-xl pointer-events-none select-none"
                  >
                    {burst.emoji}
                  </motion.span>
                );
              })}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Floating Drag Target Indicator Guide */}
      <AnimatePresence>
        {draggingEmoji && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="fixed top-16 md:top-20 left-1/2 -translate-x-1/2 z-[9990] pointer-events-none"
          >
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-950/95 border border-cyan-500/50 rounded-full shadow-[0_0_25px_rgba(6,182,212,0.4)] backdrop-blur-xl text-white">
              <span className="text-xl animate-pulse">{draggingEmoji.emoji}</span>
              <div className="flex flex-col text-left leading-tight">
                <span className="text-xs font-bold text-cyan-300">
                  {hoveredTarget ? hoveredTarget : "Kartların veya oyuncunun üzerine bırakın"}
                </span>
                <span className="text-[9px] text-slate-400 font-medium">
                  {draggingEmoji.name} Tepkisi
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Ready Emoji Reaction Dock */}
      <div
        className={`fixed z-[120] pointer-events-auto transition-all duration-300 ${
          isLandscapeLayout
            ? "bottom-3 left-4 md:left-6"
            : "bottom-16 md:bottom-20 left-3 md:left-4"
        }`}
      >
        <motion.div
          layout
          className="flex items-center gap-1.5 p-1.5 md:p-2 bg-slate-900/90 hover:bg-slate-900/95 border border-slate-700/60 rounded-2xl md:rounded-3xl shadow-[0_10px_30px_rgba(0,0,0,0.6)] backdrop-blur-xl transition-shadow"
        >
          {/* Fold / Unfold Toggle Button */}
          <button
            type="button"
            onClick={() => {
              setIsExpanded(!isExpanded);
              soundManager.play("click");
            }}
            className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 bg-slate-800/80 hover:bg-slate-700/80 text-cyan-400 rounded-xl transition-all border border-slate-700/50 shadow-inner group"
            title={isExpanded ? "Tepkileri Gizle" : "Hızlı Tepkiler"}
          >
            {isExpanded ? (
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            ) : (
              <div className="flex items-center gap-0.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                <ChevronRight className="w-3.5 h-3.5 text-cyan-400" />
              </div>
            )}
          </button>

          {/* 5 Draggable Ready Emoji Buttons */}
          <AnimatePresence mode="popLayout">
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: -10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.8, x: -10 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1 md:gap-1.5"
              >
                {READY_EMOJIS.map((reaction) => {
                  return (
                    <motion.div
                      key={reaction.id}
                      drag
                      dragSnapToOrigin
                      dragElastic={0.12}
                      dragMomentum={false}
                      whileDrag={{
                        scale: 1.45,
                        zIndex: 9999,
                        filter: `drop-shadow(0 0 15px ${reaction.glowShadow})`,
                      }}
                      whileHover={{ scale: 1.15, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onDragStart={() => handleDragStart(reaction)}
                      onDrag={handleDrag}
                      onDragEnd={(e, info) => handleDragEnd(e, info, reaction)}
                      onClick={() => handleEmojiClick(reaction)}
                      className={`relative flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-xl md:rounded-2xl cursor-grab active:cursor-grabbing select-none touch-none bg-gradient-to-b ${reaction.bgGradient} border ${reaction.ringColor} shadow-md transition-colors group`}
                      title={`${reaction.name} (Dokun veya kartların/oyuncuların üzerine sürükle)`}
                    >
                      <span className="text-xl md:text-2xl filter drop-shadow select-none pointer-events-none group-hover:scale-110 transition-transform">
                        {reaction.emoji}
                      </span>

                      {/* Tooltip Label */}
                      <span className="absolute -top-7 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-[9px] font-bold text-white bg-slate-950/90 border border-slate-700/60 px-1.5 py-0.5 rounded-md whitespace-nowrap shadow-lg">
                        {reaction.name}
                      </span>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
};
